import JSZip from "jszip";
import { AxiosResponse } from "axios";

import { axiosInstance } from "src/services/config";
import { ServicesURL } from "@utils/utilsApi";
import {
  BrandAssetContent,
  CorporateBrandAssetDto,
  FileBase64,
  LogoType,
} from "./types";
import {
  COLORS_SUFFIX,
  DEFAULT_LOGO_HEIGHT,
  LOGO_DESCRIPTORS,
  PARAMETERS_SUFFIX,
  PARAM_LOGO_HEIGHT_KEY,
  buildAssetName,
  buildZipName,
} from "./constants";

const stripDataUrl = (dataUrl: string): string => {
  const idx = dataUrl.indexOf(",");
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const buildBrandAssetZip = async (
  corporateName: string,
  content: BrandAssetContent,
): Promise<Blob> => {
  const zip = new JSZip();

  zip.file(
    buildAssetName(corporateName, COLORS_SUFFIX, "json"),
    JSON.stringify(content.colors, null, 2),
  );
  zip.file(
    buildAssetName(corporateName, PARAMETERS_SUFFIX, "json"),
    JSON.stringify(content.parameters, null, 2),
  );

  LOGO_DESCRIPTORS.forEach(({ type, suffix, ext }) => {
    const dataUrl = content.logos[type];
    if (dataUrl) {
      zip.file(
        buildAssetName(corporateName, suffix, ext),
        stripDataUrl(dataUrl),
        {
          base64: true,
        },
      );
    }
  });

  return zip.generateAsync({ type: "blob" });
};

export const blobToFileBase64 = async (
  blob: Blob,
  corporateName: string,
): Promise<FileBase64> => {
  const dataUrl = await blobToDataUrl(blob);
  return {
    nomeFile: buildZipName(corporateName).replace(/\.zip$/i, ""),
    tipoFile: "zip",
    contentFile: stripDataUrl(dataUrl),
  };
};

export const buildFileZip = async (
  corporateName: string,
  content: BrandAssetContent,
): Promise<FileBase64> => {
  const blob = await buildBrandAssetZip(corporateName, content);
  return blobToFileBase64(blob, corporateName);
};

export class InvalidBrandAssetZipError extends Error {
  missing: string[];
  constructor(missing: string[]) {
    super("INVALID_BRAND_ASSET_ZIP");
    this.name = "InvalidBrandAssetZipError";
    this.missing = missing;
  }
}

const matchLogoType = (fileName: string): LogoType | undefined => {
  const upper = fileName.toUpperCase();
  return LOGO_DESCRIPTORS.find((d) => upper.includes(d.suffix))?.type;
};

export const parseBrandAssetZip = async (
  blobData: Blob,
): Promise<BrandAssetContent> => {
  const zip = await JSZip.loadAsync(blobData);

  const content: BrandAssetContent = {
    colors: {},
    parameters: { [PARAM_LOGO_HEIGHT_KEY]: DEFAULT_LOGO_HEIGHT },
    logos: { rgb: "", white: "", black: "", favicon: "", mobile: "" },
  };

  let colorsFound = false;

  for (const fileName of Object.keys(zip.files)) {
    const entry = zip.files[fileName];
    if (entry.dir) continue;
    const upper = fileName.toUpperCase();

    if (upper.includes(COLORS_SUFFIX)) {
      content.colors = JSON.parse(await entry.async("string"));
      colorsFound = true;
    } else if (upper.includes(PARAMETERS_SUFFIX)) {
      const parsed = JSON.parse(await entry.async("string"));
      content.parameters = {
        ...parsed,
        [PARAM_LOGO_HEIGHT_KEY]:
          parsed?.[PARAM_LOGO_HEIGHT_KEY] ?? DEFAULT_LOGO_HEIGHT,
      };
    } else {
      const type = matchLogoType(fileName);
      if (type) {
        const descriptor = LOGO_DESCRIPTORS.find((d) => d.type === type)!;
        const base64 = await entry.async("base64");
        content.logos[type] = `data:${descriptor.mime};base64,${base64}`;
      }
    }
  }
  if (!colorsFound) throw new Error("MISSING_COLORS_FILE");

  return content;
};

export const downloadAndExtractBrandAsset = async (
  corporateId: number,
  zipName: string,
): Promise<BrandAssetContent> => {
  const url = `${ServicesURL.getDownloadSmartConfigurator}?corporateId=${corporateId}&fileName=${encodeURIComponent(
    zipName,
  )}`;
  const res: AxiosResponse = await axiosInstance.get(url);
  const downloadUrl: string | undefined = res.data?.public_url;
  if (!downloadUrl) throw new Error("Brand asset download url not found");

  const s3Response = await fetch(downloadUrl);
  return parseBrandAssetZip(await s3Response.blob());
};

export const extractBrandAssetContent = (
  dto: CorporateBrandAssetDto,
): Promise<BrandAssetContent> =>
  downloadAndExtractBrandAsset(dto.corporateId, dto.zipName);
